import { z } from "zod";

export const HealthCheckResponse = z.object({
  ok: z.boolean(),
  service: z.string(),
  version: z.string(),
  timestamp: z.string().datetime(),
});

export type HealthCheckResponse = z.infer<typeof HealthCheckResponse>;

export {
  ADMIN_ORDERS_PAGE_SIZE,
  ADMIN_STATS_DAILY_DAYS,
  AdminCatalogAttributes,
  AdminCatalogResponse,
  AdminCatalogType,
  AdminCatalogTypeUpdateRequest,
  AdminCatalogVariant,
  AdminCatalogVariantUpdateRequest,
  AdminErrorEnvelope,
  AdminFeatureFlag,
  AdminFeatureFlagsResponse,
  AdminFeatureFlagUpdateRequest,
  AdminFulfillmentLineTask,
  AdminFulfillmentStep,
  AdminLoginRequest,
  AdminLoginResponse,
  AdminMeResponse,
  AdminOrderDetail,
  AdminOrderLine,
  AdminOrderListItem,
  AdminOrderListQuery,
  AdminOrderListResponse,
  AdminOrderNote,
  AdminOrderNoteRequest,
  AdminOrderStepRequest,
  AdminProductionFile,
  AdminProductionFilesResponse,
  AdminProductionNote,
  AdminProductionNoteRequest,
  AdminProductionNotesResponse,
  AdminStatsDailyPoint,
  AdminStatsResponse,
  AdminStatsStatusCount,
  AdminTimelineEntry,
} from "./admin.js";
export type { ApiErrorCode } from "./api-errors.js";
export {
  API_ERROR_CODES,
  ApiError,
  ApiErrorCodeSchema,
  ApiErrorEnvelope,
} from "./api-errors.js";
export type { ArAssetFormat } from "./ar-assets.js";
export {
  AR_ASSET_EXTENSION,
  AR_ASSET_FORMATS,
  AR_ASSET_GATEWAY_PREFIX,
  AR_ASSET_MIME,
  AR_ASSET_NAME_PATTERN,
  AR_ASSET_PREFIX,
  ArAssetFormatSchema,
  ArAssetParkResponse,
  arAssetGatewayPath,
  arAssetKey,
  isArAssetName,
} from "./ar-assets.js";
export type { DeliveryMethod } from "./cart.js";
export {
  CartItem,
  CartQuoteLine,
  CartQuoteRequest,
  CartQuoteResponse,
  DELIVERY_METHODS,
  DeliveryMethodSchema,
} from "./cart.js";
export type {
  CartShipping,
  CartShippingItem,
  CartShippingSettings,
} from "./cart-shipping.js";
export { cartShippingAmountFor, shippingForCart } from "./cart-shipping.js";
export {
  CheckoutCreateRequest,
  CheckoutCreateResponse,
  CheckoutQuote,
  CheckoutSelectionRequest,
  DELIVERY_METHOD_LABEL_PL,
  OrderDelivery,
  PHONE_RE,
  PL_POST_CODE_RE,
  shippingCentsFor,
  VariantPricing,
} from "./checkout.js";
export { ClientConfig } from "./client-config.js";
export {
  CustomerLadderStop,
  CustomerOrderLine,
  CustomerOrderTracking,
  CustomerOrderView,
} from "./customer-order.js";
export type {
  AssetType,
  FabricType,
  JobStatus,
  OrderEventType,
  OrderStatus,
  PillowShape,
  PipelineStage,
  ProductionFileKind,
  ProductionNoteSource,
} from "./enums.js";
export {
  ASSET_TYPES,
  AssetTypeSchema,
  FABRIC_TYPES,
  FabricTypeSchema,
  JOB_STATUSES,
  JobStatusSchema,
  ORDER_EVENT_TYPES,
  ORDER_STATUSES,
  OrderEventTypeSchema,
  OrderStatusSchema,
  PILLOW_SHAPES,
  PIPELINE_STAGES,
  PillowShapeSchema,
  PipelineStageSchema,
  PRODUCTION_FILE_KINDS,
  PRODUCTION_NOTE_SOURCES,
  ProductionFileKindSchema,
  ProductionNoteSourceSchema,
} from "./enums.js";
export type {
  CustomerLadderProjection,
  CustomerLadderStep,
  FulfillmentEvent,
  FulfillmentLineInput,
  FulfillmentLineTask,
  FulfillmentProjection,
  FulfillmentScope,
  FulfillmentStep,
  FulfillmentStepState,
  OrderFulfillmentProjection,
  ProductionTaskKind,
  StepAction,
  StepActionContext,
  StepActionResult,
} from "./fulfillment.js";
export {
  CUSTOMER_LADDER_STOPS,
  FULFILLMENT_STEP_KEYS,
  FULFILLMENT_STEPS,
  PRODUCTION_TASK_KINDS,
  projectCustomerLadder,
  projectFulfillment,
  projectOrderFulfillment,
  resolveStepAction,
  taskKindForFulfillmentPath,
} from "./fulfillment.js";
export {
  JobAssets,
  JobClaimRequest,
  JobClaimResponse,
  JobCompleteRequest,
  JobEventPayload,
  JobEventPostBody,
  JobManifest,
} from "./jobs.js";
export type { LegalDocumentSlug } from "./legal.js";
export {
  AdminLegalDocument,
  AdminLegalDocumentPublishRequest,
  AdminLegalDocumentSaveDraftRequest,
  AdminLegalDocumentSummary,
  AdminLegalDocumentsResponse,
  LEGAL_DOCUMENT_CONTENT_MAX,
  LEGAL_DOCUMENT_SLUGS,
  LEGAL_DOCUMENT_TITLE_PL,
  LegalDocumentSlugSchema,
  PublicLegalDocument,
} from "./legal.js";
export type { NoteSourceEvent, OrderNote } from "./notes.js";
export { INTERNAL_NOTE_EVENT_TYPE, projectNotes } from "./notes.js";
export type {
  FulfillmentPath,
  PillowProductConfig,
  ProductType,
  ResolvedPillowVariant,
  ThreeDPrintFrame,
  ThreeDPrintProductConfig,
  ThreeDPrintVariantSelection,
} from "./products.js";
export {
  applyVariantToConfig,
  FULFILLMENT_PATHS,
  FulfillmentPathSchema,
  PillowVariantSelection,
  PRODUCT_TYPES,
  ProductConfig,
  ProductCreateRequest,
  ProductCreateResponse,
  ProductPatchRequest,
  ProductPatchResponse,
  ProductTypeSchema,
  THREE_D_PRINT_FRAMES,
  ThreeDPrintCreateRequest,
  ThreeDPrintFrameSchema,
  ThreeDPrintProductConfig as ThreeDPrintProductConfigSchema,
  ThreeDPrintVariantSelection as ThreeDPrintVariantSelectionSchema,
} from "./products.js";
export type {
  ProjectConfigPattern,
  ProjectConfigPetNameFont,
  ProjectConfigPetNamePosition,
  ProjectConfigSize,
} from "./project-config.js";
export {
  defaultProjectConfig,
  PROJECT_CONFIG_PATTERNS,
  PROJECT_CONFIG_PET_NAME_FONTS,
  PROJECT_CONFIG_PET_NAME_POSITIONS,
  PROJECT_CONFIG_SIZES,
  ProjectConfig,
  ProjectSizeCm,
} from "./project-config.js";
export type {
  SettingDefinition,
  SettingInputType,
  SettingKey,
  SettingsValues,
  SettingValue,
} from "./settings.js";
export {
  AdminSettingField,
  AdminSettingsResponse,
  AdminSettingUpdateRequest,
  AdminSettingUpdateResponse,
  CHECKOUT_MAX_QUANTITY_LIMIT,
  resolveSetting,
  resolveSettings,
  SETTING_INPUT_TYPES,
  SETTING_KEYS,
  SETTINGS_REGISTRY,
  SettingInputTypeSchema,
  settingsRegistryMetadata,
} from "./settings.js";
export type { SignInput, SignOutput } from "./sign-internal.js";
export { signInternal } from "./sign-internal.js";
export type { SilhouetteBoxesCm, SilhouetteRangeCm, SilhouetteTier } from "./silhouette-sizing.js";
export {
  SHIPPING_BOX_CM,
  SILHOUETTE_DEFAULT_BOXES,
  SILHOUETTE_DEFAULT_T,
  SILHOUETTE_SMALL_BOX_CM,
  SILHOUETTE_SMALL_FRACTION,
  silhouetteLongCmForT,
  silhouetteMarkerT,
  silhouetteMaxLongCm,
  silhouetteRangeCm,
  silhouetteTierForDimsCm,
  silhouetteTierForLongCm,
  silhouetteTierForT,
} from "./silhouette-sizing.js";
export type { StaticRectangleSize } from "./static-assets.js";
export {
  STATIC_RECTANGLE_MANIFEST_KEY,
  STATIC_RECTANGLE_MANIFEST_VERSION,
  STATIC_RECTANGLE_SIZE_CM,
  STATIC_RECTANGLE_SIZES,
  STATIC_RECTANGLES_PREFIX,
  StaticRectangleAssets,
  StaticRectangleManifest,
  StaticRectangleSizeSchema,
  staticRectangleKey,
} from "./static-assets.js";
export { UploadCreateRequest, UploadCreateResponse } from "./uploads.js";
export type {
  VariantAttributesValidation,
  VariantAxis,
  VariantSchemaDefinition,
} from "./variant-schema.js";
export {
  VariantAxisSchema,
  VariantSchemaDefinition as VariantSchemaDefinitionSchema,
  validateVariantAttributes,
} from "./variant-schema.js";
export type {
  PillowVariantAttributes,
  PillowVariantMatch,
  PillowVariantSizeCode,
  ThreeDPrintColorDepth,
  ThreeDPrintSizeCode,
  ThreeDPrintVariantAttributes,
  ThreeDPrintVariantMatch,
  VariantAttributes,
  VariantMatch,
  VariantResolvable,
} from "./variants.js";
export {
  cartVariantDimensionsCm,
  DEFAULT_SHIPPING_SIZE_UNITS,
  PILLOW_VARIANT_CATALOG,
  PILLOW_VARIANT_SIZE_CODES,
  PillowVariantAttributes as PillowVariantAttributesSchema,
  productLabelPl,
  resolveVariant,
  THREE_D_PRINT_COLOR_DEPTHS,
  THREE_D_PRINT_SIZE_CODES,
  THREE_D_PRINT_VARIANT_CATALOG,
  ThreeDPrintVariantAttributes as ThreeDPrintVariantAttributesSchema,
  variantShippingSizeUnits,
  variantTitlePl,
} from "./variants.js";
